import React, { useState } from 'react';
import { Box, Container, Typography, Link, Divider, Snackbar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import FeedbackIcon from '@mui/icons-material/Feedback';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { FeedbackModal } from './FeedbackModal';


export const Footer: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [iosSnackOpen, setIosSnackOpen] = useState(false);

  const handleIosClick = () => {
    setIosSnackOpen(true);
  };

  return (
    <Box 
      component="footer" 
      sx={{ 
        background: 'linear-gradient(135deg, #673AB7 0%, rgba(103,58,183,0.85) 100%)', 
        color: 'white',
        py: 5,
        mt: 'auto'
      }}
    >
      <Container maxWidth="lg">
        <Box 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)'
            },
            gap: 4
          }}
        >
          {/* Quick Links Section */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              {t('quick_links')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Link href="/" color="inherit" underline="hover" sx={{ fontSize: '0.9375rem' }}>
                {t('home')}
              </Link>
              <Link href="/schemes" color="inherit" underline="hover" sx={{ fontSize: '0.9375rem' }}>
                {t('government_schemes')}
              </Link>
              <Link href="/about" color="inherit" underline="hover" sx={{ fontSize: '0.9375rem' }}>
                {t('about')}
              </Link>
            </Box>
          </Box>

          {/* Support Section */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              {t('support')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Link href="/contact" color="inherit" underline="hover" sx={{ fontSize: '0.9375rem' }}>
                {t('contact_support')}
              </Link>
              <Link href="/contact" color="inherit" underline="hover" sx={{ fontSize: '0.9375rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t('faqs')}
              </Link>
              <Link 
                component="button"
                onClick={() => setFeedbackModalOpen(true)}
                color="inherit" 
                underline="hover" 
                sx={{ 
                  fontSize: '0.9375rem', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  textAlign: 'left'
                }}
              >
                {t('feedback')}
              </Link>
            </Box>
          </Box>

          {/* Legal Section */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              {t('legal')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Link href="/privacy_policy" color="inherit" underline="hover" sx={{ fontSize: '0.9375rem' }}>
                {t('privacy_policy')}
              </Link>
              <Link href="/terms_of_service" color="inherit" underline="hover" sx={{ fontSize: '0.9375rem' }}>
                {t('terms_of_service')}
              </Link>
            </Box>
          </Box>

          {/* Mobile Apps Section */}
          <Box>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
              {t('download_app')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1.5 }}>
              <Link 
                href="https://indusapp.store/oiz29k0p" 
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  color: 'inherit',
                  textDecoration: 'none',
                  borderRadius: 1,
                  p: 1,
                  transition: 'all 0.3s',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    borderColor: 'rgba(255,255,255,0.6)'
                  }
                }}
              >
                <AndroidIcon sx={{ fontSize: '2rem' }} />
              </Link>
              
              <Link
                component="button"
                onClick={handleIosClick}
                color="inherit"
                underline="none"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'inherit',
                  textDecoration: 'none',
                  borderRadius: 1,
                  p: 1,
                  transition: 'all 0.3s',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    borderColor: 'rgba(255,255,255,0.6)'
                  }
                }}
              >
                <AppleIcon sx={{ fontSize: '2rem' }} />
              </Link>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ width: '100%', bgcolor: 'rgba(255,255,255,0.2)', my: 3 }} />
        
        <Typography variant="body2" align="center">
          © {currentYear} {t('nagrik_sahayak')}. {t('all_rights_reserved')}
        </Typography>
      </Container>

      {/* Feedback Modal */}
      <FeedbackModal 
        open={feedbackModalOpen} 
        onClose={() => setFeedbackModalOpen(false)} 
      />

      <Snackbar
        open={iosSnackOpen}
        autoHideDuration={3000}
        onClose={() => setIosSnackOpen(false)}
        message={t('ios_coming_soon')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};
